import { useUpdateMediaLibFileMutation } from "@api/media-library";
import Icon from "@components/ui/icon";
import Popover from "@components/ui/popover";
import { sendMediaLibUploadingToast } from "@editor/modules/media-lib/utils";
import { cn } from "@lib/cn";
import { useAppDispatch } from "@store";
import { openDialog } from "@store/dialogs";
import { useMemo, useState } from "react";
import { toast } from "react-toastify";
import { track } from "@/features/analytics";
import { useAuthStoreShallow } from "@/features/authentication";
import type { IMediaFile } from "@/shared/types";
import { isFontMimeType } from "@/shared/utils/font-mime-type";
import {
  MediaLibFileArchiveDialog,
  MediaLibFileDeleteDialog,
  MediaLibFileMoveDialog,
  MediaLibFileRenameDialog,
} from "../dialogs";
import { useMediaLibFilesCtx } from "../media-lib-files.ctx";

interface IProps {
  fileData: IMediaFile;
}

interface IAction {
  action:
    | "move-to-folder"
    | "rename"
    | "archive"
    | "restore"
    | "delete"
    | "copy-link";
  icon: string;
  label: string;
  isArchived?: boolean;
}

const ACTIONS: IAction[] = [
  {
    action: "move-to-folder",
    icon: "ri-folder-transfer-line",
    label: "Move to a folder",
  },
  {
    action: "rename",
    icon: "ri-edit-line",
    label: "Rename file",
  },
  {
    action: "copy-link",
    icon: "ri-link",
    label: "Copy link",
  },
  {
    action: "archive",
    icon: "ri-archive-line",
    label: "Archive file",
  },
  //
  {
    action: "restore",
    icon: "ri-archive-line",
    label: "Restore",
    isArchived: true,
  },
  {
    action: "delete",
    icon: "ri-archive-line",
    label: "Delete",
    isArchived: true,
  },
];

export const MediaLibFileActions = ({ fileData }: IProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const isAdmin = useAuthStoreShallow((s) => s.isAdmin);

  const filledActions = useMemo(
    () =>
      ACTIONS.filter((act) => {
        const archivedMatch = (act.isArchived || false) === fileData.archived;

        // Keyed on the MIME type rather than `typeGroup`: fonts uploaded
        // before the type was canonicalised are stored with an
        // `application/font-woff`-style type, so their `typeGroup` is
        // `application` and they would never offer the link.
        if (
          act.action === "copy-link" &&
          (!isAdmin || !isFontMimeType(fileData.type))
        ) {
          return false;
        }

        return archivedMatch;
      }),
    [fileData, isAdmin],
  );

  if (filledActions.length === 0) return null;

  return (
    <div
      role="presentation"
      onClick={(e) => {
        e.stopPropagation();
      }}
      className={cn({
        "tw-absolute tw-right-1 tw-top-1 tw-z-20 tw-transition-opacity group-hover:tw-opacity-100": true,
        "tw-opacity-0": !isOpen,
      })}
    >
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <Popover.Trigger>
          <button
            type="button"
            className={cn({
              "tw-size-6 tw-cursor-pointer tw-rounded-md tw-border tw-outline-none": true,
              "tw-border-[#E7E6EC]/50 tw-bg-white tw-shadow-main": !isOpen,
              "tw-border-blue-300 tw-bg-blue-200": isOpen,
            })}
          >
            <Icon
              icon="ri-more-fill"
              className="tw-text-[14px]/none tw-text-blue-base"
            />
          </button>
        </Popover.Trigger>

        <Popover.Content className="tw-z-[99999] tw-w-[160px] tw-rounded-lg tw-border tw-border-solid tw-border-blue-100 tw-bg-white tw-p-2 tw-shadow-popover">
          <ol className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-space-y-1">
            {filledActions.map((act) => (
              <Action
                key={act.action}
                fileData={fileData}
                action={act.action}
                icon={act.icon}
                label={act.label}
                close={() => setIsOpen(false)}
              />
            ))}
          </ol>
        </Popover.Content>
      </Popover>
    </div>
  );
};

interface IActionProps extends IAction {
  fileData: IMediaFile;
  close: () => void;
}

const Action = ({ close, action, icon, label, fileData }: IActionProps) => {
  const { updateFile: updateFileLocally, removeFile: removeFileLocally } =
    useMediaLibFilesCtx();
  const dispatch = useAppDispatch();

  const [updateFile, { isLoading: isUpdatingFile }] =
    useUpdateMediaLibFileMutation();

  const handleAction = async () => {
    if (isUpdatingFile) return;

    switch (action) {
      case "move-to-folder":
        dispatch(
          openDialog({
            id: MediaLibFileMoveDialog.dialogId,
            content: MediaLibFileMoveDialog,
            props: {
              fileData,
              onUpdateFile: updateFileLocally,
            },
          }),
        );
        break;

      case "rename":
        dispatch(
          openDialog({
            id: MediaLibFileRenameDialog.dialogId,
            content: MediaLibFileRenameDialog,
            props: {
              fileData,
              onUpdateFile: updateFileLocally,
            },
          }),
        );
        break;
      case "archive":
        dispatch(
          openDialog({
            id: MediaLibFileArchiveDialog.dialogId,
            content: MediaLibFileArchiveDialog,
            props: {
              fileData,
              onRemoveFile: () => {
                updateFileLocally(fileData._id, {
                  archived: true,
                });
              },
            },
          }),
        );
        break;
      case "restore": {
        const toastId = sendMediaLibUploadingToast({
          msg: "Restoring media...",
        });
        try {
          close();
          await updateFile({
            id: fileData._id,
            data: {
              archived: false,
              folderId: null,
            },
          }).unwrap();
          updateFileLocally(fileData._id, {
            folderId: null,
            archived: false,
          });
        } catch (e) {
          track("error restoring media file", {
            fileData,
            error: e,
          });
          toast.error("Error restoring file");
        }
        toast.dismiss(toastId);

        break;
      }
      case "delete":
        dispatch(
          openDialog({
            id: MediaLibFileDeleteDialog.dialogId,
            content: MediaLibFileDeleteDialog,
            props: {
              fileData,
              onRemoveFile: removeFileLocally,
            },
          }),
        );
        break;
      case "copy-link":
        navigator.clipboard.writeText(fileData.url);
        close();
        toast.success("Link copied to clipboard");
        break;
    }
    close();
  };

  const isDanger = action === "archive" || action === "delete";
  return (
    <li
      role="menuitem"
      tabIndex={0}
      className={cn(
        "tw-flex tw-cursor-pointer tw-items-center tw-space-x-2 tw-rounded tw-px-3 tw-py-[10px] tw-transition-colors",
        isDanger ? "hover:tw-bg-[#FFEEF0]" : "hover:tw-bg-blue-50",
      )}
      onClick={handleAction}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleAction(); }}
    >
      <Icon
        icon={icon}
        className={cn(
          "tw-text-[16px]/none",
          isDanger ? "tw-text-[#D43548]" : " tw-text-blue-500",
        )}
      />
      <span
        className={cn(
          "tw-text-[11px]",
          isDanger ? "tw-text-[#D43548]" : "tw-text-blue-base",
        )}
      >
        {label}
      </span>
    </li>
  );
};
